import React from "react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Upload, Download, Loader2, ImageIcon, Sparkles, Lock, ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  HALFTONE_STYLES, DEFAULT_HALFTONE_PARAMS,
  renderHalftone, loadImageFromFile, downloadCanvas,
} from "@/lib/halftone";

/**
 * Interactive Try-It widget for the landing page hero.
 * Fully client-side (no signup wall). Free preview shows a light watermark
 * across the export. Signed-in users get clean exports in the full Studio.
 */
export default function TryItWidget() {
  const inputRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const [img, setImg] = React.useState(null);
  const [style, setStyle] = React.useState("am-round");
  const [busy, setBusy] = React.useState(false);
  const [ready, setReady] = React.useState(false);

  const runHalftone = React.useCallback(async (image) => {
    if (!image || !canvasRef.current) return;
    setBusy(true);
    // small yield so spinner paints
    await new Promise((r) => setTimeout(r, 50));
    try {
      renderHalftone(image, canvasRef.current, {
        ...DEFAULT_HALFTONE_PARAMS,
        fgColor: "#0a0d15",
        lpi: 32, angle: 22.5, bgTransparent: false,
      }, style);
      // Watermark overlay for the preview
      const ctx = canvasRef.current.getContext("2d");
      const w = canvasRef.current.width, h = canvasRef.current.height;
      ctx.save();
      ctx.globalAlpha = 0.18;
      ctx.font = `${Math.max(18, w * 0.05)}px "Space Grotesk", sans-serif`;
      ctx.fillStyle = "#1e6fff";
      ctx.textAlign = "center";
      ctx.translate(w / 2, h / 2);
      ctx.rotate(-Math.PI / 8);
      ctx.fillText("furnacedesignlab.app", 0, 0);
      ctx.restore();
      setReady(true);
    } finally {
      setBusy(false);
    }
  }, [style]);

  React.useEffect(() => {
    if (img) runHalftone(img);
  }, [img, style, runHalftone]);

  const onFile = async (file) => {
    if (!file) return;
    try {
      const image = await loadImageFromFile(file);
      setImg(image);
    } catch {
      // ignore
    }
  };

  return (
    <div className="rounded-3xl chrome-border p-3 shadow-2xl shadow-blue-950/40">
      <div
        className="relative overflow-hidden rounded-2xl"
        style={{
          backgroundImage:
            "repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%)",
          backgroundSize: "16px 16px",
          backgroundPosition: "50%",
        }}
      >
        {!img ? (
          <label
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
            className="flex h-[420px] cursor-pointer flex-col items-center justify-center gap-3 p-8 text-center"
          >
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#4aa4ff]/15 text-[#4aa4ff]">
              <ImageIcon className="h-6 w-6" />
            </span>
            <div className="display-font text-xl font-bold text-[#05070d]">Try it — no signup.</div>
            <div className="max-w-sm text-sm text-black/70">
              Drop any PNG or JPG here. We’ll render a live halftone right in your browser.
            </div>
            <Button
              type="button"
              data-testid="tryit-choose-image-btn"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); inputRef.current?.click(); }}
              className="btn-ember h-11 gap-2 px-5 font-semibold"
            >
              <Upload className="h-4 w-4" /> Choose image
            </Button>
            <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => onFile(e.target.files?.[0])} />
          </label>
        ) : (
          <div className="relative flex h-[420px] items-center justify-center p-3">
            {busy && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
                <Loader2 className="h-6 w-6 animate-spin text-[#1e6fff]" />
              </div>
            )}
            <canvas ref={canvasRef} className="max-h-[400px] max-w-full rounded-md" />
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-3">
        <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Style</span>
        <div className="min-w-[180px]">
          <Select value={style} onValueChange={setStyle}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {HALFTONE_STYLES.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        {img && (
          <Button variant="outline" className="ml-1 h-9 gap-2 text-xs" onClick={() => inputRef.current?.click()}>
            <Upload className="h-3.5 w-3.5" /> New image
          </Button>
        )}
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => onFile(e.target.files?.[0])} />

        <div className="ml-auto flex items-center gap-2">
          {ready && (
            <Button variant="outline" className="h-9 gap-2 text-xs" onClick={() => downloadCanvas(canvasRef.current, `preview_${style}.png`)}>
              <Download className="h-3.5 w-3.5" /> Watermarked preview
            </Button>
          )}
          <Link to="/signup">
            <Button className="btn-ember h-9 gap-2 text-xs font-semibold">
              <Lock className="h-3.5 w-3.5" /> Sign up for clean export <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-2 px-1 text-[11px] text-muted-foreground">
        <Sparkles className="h-3 w-3 text-[#4aa4ff]" /> Rendered on your device · nothing uploaded · free tier gets 3 clean exports/mo
      </div>
    </div>
  );
}
